/**
 * アプリ版の広告と課金。
 *
 * - 広告：AdMob（@capacitor-community/admob）。iOS・Android とも
 * - 課金：App Store（@capgo/native-purchases、StoreKit 2）。**iOS だけ**。
 *   Android は Google Play で課金を有効にすると販売者の住所が公開されるため、広告だけで出す（2026-09-24 決定）
 *
 * プラグインは使うときに import する（Web版のバンドルに混ぜない）。
 *
 * 起動時の順番（2026-10-20 審査の差し戻し Guideline 2.1 を受けて変更）：
 *   1. 同意の確認（UMP。日本だけで配っていれば普通は「不要」で終わる）
 *   2. iOS のトラッキング許可（ATT）。アプリが前面に出て（appStateChange の isActive）から少し待って聞く
 *   3. ATT の返事が来てから AdMob の初期化 → 次に出す広告を先に読み込む
 *
 * 以前は「初めてのゲームオーバーのあと」に ATT を聞いていたが、審査で「許可のダイアログが見つからない」と
 * 差し戻された。起動直後に聞き、ATT より前に広告SDKを初期化しない（データを集めない）形にした。
 */

import type {
  LocalNotice,
  Platform,
  PurchaseOutcome,
  RewardedPlacement,
  TrackingResult,
  TrackingStatus,
} from './Platform';
import { PRODUCTS, type ProductId } from './products';
import { adUnitsFor, type AdUnits } from './adConfig';
import { notifyAdCovering, notifyAdUnavailable } from './adLifecycle';
import type { NativeOS } from './native';

type AdMobModule = typeof import('@capacitor-community/admob');
type PurchasesModule = typeof import('@capgo/native-purchases');
type NotificationsModule = typeof import('@capacitor/local-notifications');
type Handle = { remove: () => Promise<void> };

/** 広告の読み込みを待つ上限。電波が悪いときに延々と待たせない。 */
const LOAD_TIMEOUT_MS = 8_000;
/** 出した広告が閉じられるのを待つ上限（閉じた合図を取りこぼしても固まらないように）。 */
const SHOW_TIMEOUT_MS = 120_000;
/** ネイティブ側の問い合わせ（同意の状態・初期化・値段）を待つ上限。返事が来なくても固まらないように。 */
const CALL_TIMEOUT_MS = 10_000;
/** 広告や購入のボタンを押したとき、起動時の準備を待つ上限。 */
const INIT_WAIT_MS = 15_000;
/** 「購入を復元する」で App Store との同期を待つ上限（Apple ID のサインインを含む）。 */
const RESTORE_WAIT_MS = 90_000;
/** アプリが前面に出てから ATT のダイアログを出すまでの間（直後だと iOS が出さないことがある）。 */
export const ATT_DELAY_MS = 800;
/** アプリが前面に出るのを待つ上限（返事が来なくても先へ進む）。 */
const ACTIVE_WAIT_MS = 30_000;
/** ATT のダイアログの返事を待つ上限。 */
const ATT_ANSWER_WAIT_MS = 120_000;

let admobModule: Promise<AdMobModule> | null = null;
/** AdMob のプラグインを読む（1回だけ。同意の確認・ATT・初期化で同じものを使う）。 */
function loadAdMob(): Promise<AdMobModule> {
  admobModule ??= import('@capacitor-community/admob');
  return admobModule;
}

function sleep(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

/** アプリが前面にいる（isActive）まで待つ。iOS は前面にいないと ATT のダイアログを出さない。 */
async function waitUntilActive(): Promise<void> {
  try {
    const { App } = await import('@capacitor/app');
    const state = await withTimeout(App.getState(), CALL_TIMEOUT_MS, { isActive: true });
    if (state.isActive) return;
    await new Promise<void>((resolve) => {
      let handle: Handle | null = null;
      let done = false;
      const finish = () => {
        if (done) return;
        done = true;
        clearTimeout(timer);
        void handle?.remove().catch(() => undefined);
        resolve();
      };
      const timer = setTimeout(finish, ACTIVE_WAIT_MS);
      void App.addListener('appStateChange', ({ isActive }) => {
        if (isActive) finish();
      }).then((h) => {
        handle = h;
        if (done) void h.remove().catch(() => undefined);
      });
    });
  } catch {
    /* 状態が分からなければ、そのまま進む */
  }
}

/** 診断の表示用に、エラーを短い文字列にする。 */
function errorText(error: unknown): string {
  if (error instanceof Error) return error.message.slice(0, 120);
  if (typeof error === 'object' && error && 'message' in error) return String((error as { message: unknown }).message).slice(0, 120);
  return String(error).slice(0, 120);
}

function withTimeout<T>(promise: Promise<T>, ms: number, fallback: T): Promise<T> {
  return new Promise<T>((resolve) => {
    const timer = setTimeout(() => resolve(fallback), ms);
    promise.then(
      (v) => {
        clearTimeout(timer);
        resolve(v);
      },
      () => {
        clearTimeout(timer);
        resolve(fallback);
      },
    );
  });
}

/**
 * 購入プラグインのエラーを、ショップで出し分ける結果に直す。
 * @capgo/native-purchases は iOS で「User cancelled」「Transaction pending」の文言で reject する。
 */
export function purchaseErrorOutcome(error: unknown): PurchaseOutcome {
  const text = String((error as { message?: unknown } | null)?.message ?? error ?? '').toLowerCase();
  const code = String((error as { code?: unknown } | null)?.code ?? '').toLowerCase();
  if (text.includes('cancel') || code.includes('cancel')) return 'cancelled';
  if (text.includes('pending') || code.includes('pending')) return 'pending';
  return 'failed';
}

export class NativePlatform implements Platform {
  readonly kind = 'native' as const;
  readonly monetized = true;
  readonly canPurchase: boolean;

  private readonly units: AdUnits;
  private initPromise: Promise<void> | null = null;
  private admob: AdMobModule | null = null;
  private purchases: PurchasesModule | null = null;
  /** 先読みしている広告。true なら出せる。 */
  private rewardedLoad: Promise<boolean> | null = null;
  private interstitialLoad: Promise<boolean> | null = null;
  private readonly prices = new Map<ProductId, string>();
  /** 広告の診断（テスト用ビルドの設定画面に出す。2026-10-12 合間の広告が実機で出ない件の切り分け用）。 */
  private readonly adLog: string[] = [];
  private rewardedState = '未読み込み';
  private interstitialState = '未読み込み';

  private logAd(line: string): void {
    const d = new Date();
    const hhmmss = [d.getHours(), d.getMinutes(), d.getSeconds()].map((n) => String(n).padStart(2, '0')).join(':');
    this.adLog.push(`${hhmmss} ${line}`);
    if (this.adLog.length > 40) this.adLog.shift();
  }

  /** 広告の状態（テスト用の広告IDのときだけ。本物の広告IDのビルドでは null）。 */
  adDiagnostics(): string[] | null {
    if (!this.units.testing) return null;
    return [
      `広告SDK：${this.admob ? '準備できた' : '準備できていない'}`,
      `ごほうび広告：${this.rewardedState}`,
      `合間の広告：${this.interstitialState}`,
      '--- 記録 ---',
      ...this.adLog,
    ];
  }

  constructor(readonly os: NativeOS) {
    this.canPurchase = os === 'ios';
    this.units = adUnitsFor(os);
  }

  init(): Promise<void> {
    this.initPromise ??= this.doInit();
    return this.initPromise;
  }

  private trackingAsked = false;
  /** ATT のダイアログを出している最中（レビュー依頼などを重ねないため）。 */
  private trackingTask: Promise<TrackingResult | null> = Promise.resolve(null);

  /**
   * 聞いた結果を返す（計測の att に使う）。2回目からは null（同じ結果を二度数えないため）。
   */
  askTrackingPermission(): Promise<TrackingResult | null> {
    if (this.os !== 'ios') return Promise.resolve(null);
    if (this.trackingAsked) return this.trackingTask.then(() => null);
    this.trackingAsked = true;
    this.trackingTask = (async () => {
      // 同意の確認（UMP）を先に済ませる。そのあと、アプリが前面に出てから少し待って聞く
      await this.runConsent();
      await waitUntilActive();
      await sleep(ATT_DELAY_MS);
      return this.doAskTracking();
    })();
    return this.trackingTask;
  }

  private async doAskTracking(): Promise<TrackingResult | null> {
    try {
      const mod: AdMobModule = this.admob ?? (await loadAdMob());
      const tracking = await withTimeout(mod.AdMob.trackingAuthorizationStatus(), CALL_TIMEOUT_MS, null);
      if (!tracking) return null;
      const before = tracking.status as TrackingStatus;
      if (before !== 'notDetermined') return { status: before, prompted: false };
      // ダイアログはユーザーが答えるまで返らない。長めに待つ
      this.logAd('トラッキングの確認を表示');
      await withTimeout(mod.AdMob.requestTrackingAuthorization(), ATT_ANSWER_WAIT_MS, undefined);
      const after = await withTimeout(mod.AdMob.trackingAuthorizationStatus(), CALL_TIMEOUT_MS, null);
      return { status: (after?.status as TrackingStatus | undefined) ?? 'notDetermined', prompted: true };
    } catch {
      /* 断られても・聞けなくても広告は出る（パーソナライズなし） */
      return null;
    }
  }

  /** 準備を待つ（上限つき）。待ちきれなくても準備は裏で続く。 */
  private ready(): Promise<void> {
    return withTimeout(this.init(), INIT_WAIT_MS, undefined);
  }

  private async doInit(): Promise<void> {
    await Promise.all([this.initAds(), this.canPurchase ? this.initStore() : Promise.resolve()]);
  }

  private consentTask: Promise<void> | null = null;

  /** 同意の確認（UMP）。1回だけ。失敗しても先へ進む。 */
  private runConsent(): Promise<void> {
    this.consentTask ??= (async () => {
      try {
        const { AdMob, AdmobConsentStatus } = await loadAdMob();
        const consent = await withTimeout(AdMob.requestConsentInfo(), CALL_TIMEOUT_MS, null);
        if (!consent) throw new Error('consent-timeout');
        this.logAd(`同意の確認：${consent.status}`);
        if (consent.status === AdmobConsentStatus.REQUIRED && consent.isConsentFormAvailable) {
          await AdMob.showConsentForm();
        }
      } catch (error) {
        /* 同意の確認に失敗しても、広告は「パーソナライズなし」で出る */
        this.logAd(`同意の確認に失敗：${errorText(error)}`);
      }
    })();
    return this.consentTask;
  }

  private async initAds(): Promise<void> {
    try {
      this.logAd('初期化を開始');
      const mod: AdMobModule = await loadAdMob();
      const { AdMob, MaxAdContentRating } = mod;

      // 同意の確認 → ATT の返事、の順に待ってから広告SDKを初期化する（ATT より前にデータを集めない）
      await this.runConsent();
      if (this.os === 'ios') {
        await this.askTrackingPermission();
        this.logAd('トラッキングの確認：済み');
      }

      const initialized = await withTimeout(
        AdMob.initialize({
          initializeForTesting: this.units.testing,
          // App Store の年齢区分 4+ に合わせ、広告の内容は「全年齢」に絞る（審査ガイドライン 2.5.18）
          maxAdContentRating: MaxAdContentRating.General,
        }).then(() => true),
        CALL_TIMEOUT_MS,
        false,
      );
      if (!initialized) throw new Error('admob-init-timeout');
      this.logAd('広告SDKの初期化：成功');
      this.admob = mod;
      this.preloadRewarded();
      this.preloadInterstitial();
    } catch (error) {
      this.logAd(`広告SDKの初期化に失敗：${errorText(error)}`);
      this.admob = null;
    }
  }

  private async initStore(): Promise<void> {
    await this.refreshPrices();
  }

  /** 値段の問い合わせ中（同時に何回も走らせない）。 */
  private priceTask: Promise<boolean> | null = null;

  private hasAllPrices(): boolean {
    return Object.values(PRODUCTS).every((p) => this.prices.has(p.id));
  }

  /**
   * 値段が欠けていれば、ストアにもう一度問い合わせる。
   * 起動時に電波が悪くて取れなかったときも、ショップを開けば取り直せるように（2026-10-05）。
   */
  refreshPrices(): Promise<boolean> {
    if (!this.canPurchase) return Promise.resolve(false);
    if (this.hasAllPrices()) return Promise.resolve(true);
    if (!this.priceTask) {
      this.priceTask = this.loadPrices().finally(() => {
        this.priceTask = null;
      });
    }
    return this.priceTask;
  }

  private async loadPrices(): Promise<boolean> {
    try {
      const mod: PurchasesModule = this.purchases ?? (await import('@capgo/native-purchases'));
      this.purchases = mod;
      const ids = Object.values(PRODUCTS).map((p) => p.storeId);
      const { products } = await withTimeout(
        mod.NativePurchases.getProducts({ productIdentifiers: ids, productType: mod.PURCHASE_TYPE.INAPP }),
        CALL_TIMEOUT_MS,
        { products: [] },
      );
      for (const product of products) {
        const def = Object.values(PRODUCTS).find((p) => p.storeId === product.identifier);
        if (def && product.priceString) this.prices.set(def.id, product.priceString);
      }
    } catch {
      /* ストアにつながらないときは値段が「—」のまま。ショップを開き直すと取り直す */
    }
    return this.hasAllPrices();
  }

  /* ------------------------------ 広告 ------------------------------ */

  private preloadRewarded(): void {
    const mod = this.admob;
    if (!mod) return;
    this.rewardedState = '読み込み中';
    this.rewardedLoad = mod.AdMob.prepareRewardVideoAd({
      adId: this.units.rewarded,
      isTesting: this.units.testing,
    }).then(
      () => {
        this.rewardedState = '読み込めた';
        return true;
      },
      (error: unknown) => {
        this.rewardedState = `読み込み失敗（${errorText(error)}）`;
        this.logAd(`ごほうび広告の読み込みに失敗：${errorText(error)}`);
        return false;
      },
    );
  }

  private preloadInterstitial(): void {
    const mod = this.admob;
    if (!mod) return;
    this.interstitialState = '読み込み中';
    this.interstitialLoad = mod.AdMob.prepareInterstitial({
      adId: this.units.interstitial,
      isTesting: this.units.testing,
    }).then(
      () => {
        this.interstitialState = '読み込めた';
        return true;
      },
      (error: unknown) => {
        this.interstitialState = `読み込み失敗（${errorText(error)}）`;
        this.logAd(`合間の広告の読み込みに失敗：${errorText(error)}`);
        return false;
      },
    );
  }

  /** 先読み済みならそれを使い、失敗していたら一度だけ読み直す。 */
  private async ensureLoaded(kind: 'rewarded' | 'interstitial'): Promise<boolean> {
    const current = kind === 'rewarded' ? this.rewardedLoad : this.interstitialLoad;
    if (current && (await withTimeout(current, LOAD_TIMEOUT_MS, false))) return true;
    // 読み込めていなければ、読み直しは裏で始めて、今回はすぐ「出せない」を返す
    // （以前は読み直しも待っていたので、押してから最大16秒ほど何も起きなかった。2026-10-20）
    if (kind === 'rewarded') this.preloadRewarded();
    else this.preloadInterstitial();
    return false;
  }

  async showRewarded(_placement: RewardedPlacement): Promise<boolean> {
    await this.ready();
    const mod = this.admob;
    if (!mod || !(await this.ensureLoaded('rewarded'))) {
      this.logAd('ごほうび広告を出せず（読み込めていない）');
      notifyAdUnavailable();
      return false;
    }
    const { AdMob, RewardAdPluginEvents } = mod;

    let rewarded = false;
    const handles: Handle[] = [];
    let closed!: () => void;
    const done = new Promise<void>((resolve) => (closed = resolve));
    handles.push(await AdMob.addListener(RewardAdPluginEvents.Rewarded, () => (rewarded = true)));
    handles.push(await AdMob.addListener(RewardAdPluginEvents.Dismissed, () => closed()));
    let failed = false;
    handles.push(
      await AdMob.addListener(RewardAdPluginEvents.FailedToShow, () => {
        failed = true;
        closed();
      }),
    );

    notifyAdCovering(true);
    try {
      const item = await AdMob.showRewardVideoAd();
      if (item && typeof item.amount === 'number' && item.amount > 0) rewarded = true;
      await withTimeout(done, SHOW_TIMEOUT_MS, undefined);
    } catch {
      /* 出せなかった */
      failed = true;
    } finally {
      notifyAdCovering(false);
      await Promise.all(handles.map((h) => h.remove().catch(() => undefined)));
      this.rewardedLoad = null;
      this.preloadRewarded();
    }
    if (failed && !rewarded) notifyAdUnavailable();
    return rewarded;
  }

  async showInterstitial(): Promise<boolean> {
    await this.ready();
    const mod = this.admob;
    // 合間の広告は「出せなければ出さない」。読み込みを待たせてまで出さない。
    // 2026-10-12：先読みに失敗していた（false）ときは読み直す。以前は null のときしか読み直さず、
    // 起動時の先読みが一度失敗すると、そのあと合間の広告が一度も出なくなっていた。
    // まだ読み込み中（1.5秒で終わらない＝null）のときは、そのまま待たせておく。
    const ready = this.interstitialLoad ? await withTimeout<boolean | null>(this.interstitialLoad, 1_500, null) : null;
    if (!mod || ready !== true) {
      this.logAd(`合間の広告を出せず：${!mod ? '広告SDKが準備できていない' : ready === false ? '読み込み失敗' : 'まだ読み込み中'}`);
      if (mod && (!this.interstitialLoad || ready === false)) this.preloadInterstitial();
      return false;
    }
    this.logAd('合間の広告を出す');
    let shown = false;
    const { AdMob, InterstitialAdPluginEvents } = mod;
    const handles: Handle[] = [];
    let closed!: () => void;
    const done = new Promise<void>((resolve) => (closed = resolve));
    handles.push(await AdMob.addListener(InterstitialAdPluginEvents.Dismissed, () => closed()));
    handles.push(await AdMob.addListener(InterstitialAdPluginEvents.FailedToShow, () => closed()));

    notifyAdCovering(true);
    try {
      await AdMob.showInterstitial();
      shown = true;
      await withTimeout(done, SHOW_TIMEOUT_MS, undefined);
    } catch (error) {
      this.logAd(`合間の広告の表示に失敗：${errorText(error)}`);
      /* 出せなかった */
    } finally {
      notifyAdCovering(false);
      await Promise.all(handles.map((h) => h.remove().catch(() => undefined)));
      this.interstitialLoad = null;
      this.preloadInterstitial();
    }
    return shown;
  }

  /* ------------------------------ 課金（iOS だけ） ------------------------------ */

  async purchase(productId: ProductId): Promise<PurchaseOutcome> {
    if (!this.canPurchase) return 'failed';
    await this.ready();
    const mod = this.purchases;
    if (!mod) return 'failed';
    const product = PRODUCTS[productId];
    try {
      const tx = await mod.NativePurchases.purchaseProduct({
        productIdentifier: product.storeId,
        productType: mod.PURCHASE_TYPE.INAPP,
        isConsumable: product.kind === 'consumable',
        quantity: 1,
      });
      return tx.productIdentifier === product.storeId ? 'purchased' : 'failed';
    } catch (error) {
      return purchaseErrorOutcome(error);
    }
  }

  async ownedProducts(): Promise<ProductId[]> {
    if (!this.canPurchase) return [];
    await this.ready();
    const mod = this.purchases;
    if (!mod) return [];
    try {
      // 返事が来なくても固まらないよう上限をつける（審査の環境は通信が不安定なことがある。2026-10-20）
      const { purchases } = await withTimeout(
        mod.NativePurchases.getPurchases({
          productType: mod.PURCHASE_TYPE.INAPP,
          onlyCurrentEntitlements: true,
        }),
        CALL_TIMEOUT_MS,
        { purchases: [] },
      );
      const owned = new Set<ProductId>();
      for (const tx of purchases) {
        const def = Object.values(PRODUCTS).find((p) => p.storeId === tx.productIdentifier);
        // 消耗型（おはし）は「持っている」に数えない。買った時点で本数に足している
        if (def && def.kind === 'non-consumable') owned.add(def.id);
      }
      return [...owned];
    } catch {
      return [];
    }
  }

  async restorePurchases(): Promise<ProductId[]> {
    if (!this.canPurchase) return [];
    await this.ready();
    const mod = this.purchases;
    if (!mod) throw new Error('store-unavailable');
    let synced = true;
    try {
      // App Store と同期する（Apple ID のパスワードを聞かれることがある。ボタンを押したときだけ呼ぶ）
      // Apple ID のサインインを待つことがあるので長めに。それでも返事がなければ「つながらない」として先へ
      const done = await withTimeout(
        mod.NativePurchases.restorePurchases().then(() => true),
        RESTORE_WAIT_MS,
        false,
      );
      if (!done) synced = false;
    } catch {
      /* 同期できなくても、手元で分かる分は返す */
      synced = false;
    }
    const owned = await this.ownedProducts();
    // 同期に失敗して手元にも何もない＝「見つからない」ではなく「つながらない」
    if (!synced && owned.length === 0) throw new Error('restore-failed');
    return owned;
  }

  priceOf(productId: ProductId): string | null {
    return this.prices.get(productId) ?? null;
  }

  /* ------------------------------ レビュー依頼（iOS だけ） ------------------------------ */

  async requestReview(): Promise<void> {
    // Android 版は今は Google Play に出していないので、iOS だけ
    if (this.os !== 'ios') return;
    // ATT のダイアログと重ねない
    await this.trackingTask.catch(() => undefined);
    try {
      const { InAppReview } = await import('@capacitor-community/in-app-review');
      await withTimeout(InAppReview.requestReview(), CALL_TIMEOUT_MS, undefined);
    } catch {
      /* 出せなくても何もしない */
    }
  }

  /* ------------------------------ お知らせ（iOS だけ） ------------------------------ */

  get canNotify(): boolean {
    return this.os === 'ios';
  }

  private notifications: Promise<NotificationsModule | null> | null = null;

  private loadNotifications(): Promise<NotificationsModule | null> {
    if (!this.canNotify) return Promise.resolve(null);
    this.notifications ??= import('@capacitor/local-notifications').catch(() => null);
    return this.notifications;
  }

  async requestNotificationPermission(): Promise<boolean> {
    const mod = await this.loadNotifications();
    if (!mod) return false;
    try {
      const current = await mod.LocalNotifications.checkPermissions();
      if (current.display === 'granted') return true;
      if (current.display === 'denied') return false;
      // ダイアログはユーザーが答えるまで返らない
      const asked = await withTimeout(mod.LocalNotifications.requestPermissions(), 60_000, null);
      return asked?.display === 'granted';
    } catch {
      return false;
    }
  }

  async scheduleNotices(list: readonly LocalNotice[]): Promise<void> {
    const mod = await this.loadNotifications();
    if (!mod) return;
    try {
      // 起動時に黙って呼ぶので、許可のダイアログは出さない（許可済みのときだけ予約する）
      const permission = await mod.LocalNotifications.checkPermissions();
      if (permission.display !== 'granted') return;
      await this.cancelPending(mod);
      if (list.length === 0) return;
      await mod.LocalNotifications.schedule({
        notifications: list.map((n) => ({
          id: n.id,
          title: n.title,
          body: n.body,
          schedule: { at: n.at, allowWhileIdle: true },
          extra: { href: n.href },
        })),
      });
    } catch {
      /* 予約できなくてもゲームは止めない */
    }
  }

  async cancelNotices(): Promise<void> {
    const mod = await this.loadNotifications();
    if (!mod) return;
    try {
      await this.cancelPending(mod);
    } catch {
      /* 何もしない */
    }
  }

  private async cancelPending(mod: NotificationsModule): Promise<void> {
    const { notifications } = await mod.LocalNotifications.getPending();
    if (notifications.length > 0) {
      await mod.LocalNotifications.cancel({ notifications: notifications.map((n) => ({ id: n.id })) });
    }
  }

  onNoticeOpened(handler: (href: string) => void): void {
    void this.loadNotifications().then((mod) => {
      if (!mod) return;
      void mod.LocalNotifications.addListener('localNotificationActionPerformed', (action) => {
        const href = (action.notification.extra as { href?: unknown } | undefined)?.href;
        if (typeof href === 'string' && href.startsWith('/')) handler(href);
      }).catch(() => undefined);
    });
  }
}
